import { httpRequest } from '@/shared/api/client';
import { buildQueryString } from './builder';
import { denormalize, type Denormalized } from './parser';
import type { JsonApiDocument, JsonApiResource, ListQuery } from './types';
import { getActiveTenantOverride, shouldApplyTenantOverride } from '@/shared/lib/active-tenant';

// Contratto v1.7.0: attributi/relationships in camelCase sul wire (type e route restano
// kebab-case). Nessuna conversione di casing serve piu' lato FE.

export interface CreateBody<TAttributes = Record<string, unknown>> {
  /** Client-supplied id. If absent, a UUIDv4 is generated client-side. */
  id?: string;
  attributes?: TAttributes;
  relationships?: Record<string, { data: { type: string; id: string } | Array<{ type: string; id: string }> | null }>;
}

/** Options condivise dalle helper JSON:API per disabilitare l'iniezione cross-tenant.
 *  Usare per risorse globali/user-scoped non IHasTenant (es. user-preferences). */
export interface JsonApiClientOptions {
  skipTenantOverride?: boolean;
}

/**
 * BE v1.8.3+: l'override cross-tenant si applica via filter JSON:API per le list
 * e via data.relationships.tenant per le POST. PATCH/DELETE/GET-by-id NON
 * vogliono filter/body — il BE risolve dal GUID. Questa funzione decide se
 * iniettare per la chiamata in corso.
 */
function activeOverrideFor(type: string, opts?: JsonApiClientOptions): string | null {
  if (opts?.skipTenantOverride) return null;
  if (!shouldApplyTenantOverride(type)) return null;
  return getActiveTenantOverride();
}

/**
 * Nome della relationship/campo che porta il TenantId per l'override cross-tenant.
 * Default `tenant`. `erp-sites` usa `primaryTenant` (Site Sharing v1, BE v1.17.1):
 * la vecchia relationship 1:1 `tenant` non esiste più. NB: in cross-tenant la list
 * filtra per `primaryTenant.id` → mostra le sedi POSSEDUTE dal tenant override (non
 * quelle dove è solo ospite; per quelle usare GET /api/erp-sites/byTenant).
 */
const TENANT_REL_BY_TYPE: Record<string, string> = { 'erp-sites': 'primaryTenant' };
function tenantRelName(type: string): string {
  return TENANT_REL_BY_TYPE[type] ?? 'tenant';
}

/** Generate a UUIDv4 client-side. Workaround: il BE su alcune entità ritorna 404
 *  "ID '' not found" dopo la POST se l'Id non è esplicitato dal client. Lo
 *  generiamo qui per evitare race con il default `NEWID()` di SQL Server. */
function generateUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback per ambienti senza crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export interface UpdateBody<TAttributes = Record<string, unknown>> extends CreateBody<TAttributes> {
  id: string;
}

/** Path conventions: `/api/<type>` (per JsonApiOptions.Namespace = "api" + UseRelativeLinks). */
function pathForType(type: string, id?: string): string {
  return id ? `/api/${type}/${id}` : `/api/${type}`;
}

export async function fetchList<T extends Denormalized = Denormalized>(
  type: string,
  query?: ListQuery,
  opts?: JsonApiClientOptions,
): Promise<{ data: T[]; total: number; meta: Record<string, unknown> }> {
  // Cross-tenant (BE v1.8.3+): se l'override è attivo, aggiungo filter
  // `equals(tenant.id, '<override>')` AND-ato con i filter esistenti.
  // Se il caller ha già un filter su tenant.id, prevale quello del caller.
  const override = activeOverrideFor(type, opts);
  const tField = `${tenantRelName(type)}.id`;
  const effectiveQuery: ListQuery | undefined = override
    ? {
        ...query,
        filter: {
          ...(query?.filter ?? {}),
          ...(query?.filter && tField in query.filter ? {} : { [tField]: { eq: override } }),
        },
      }
    : query;
  const doc = await httpRequest<JsonApiDocument>(`${pathForType(type)}${buildQueryString(effectiveQuery)}`);
  const data = (denormalize<T>(doc) as T[] | null) ?? [];
  return { data, total: typeof doc.meta?.total === 'number' ? doc.meta.total : data.length, meta: doc.meta ?? {} };
}

/**
 * Carica TUTTE le pagine di una lista JSON:API (page size BE max = 100, oltre → 400).
 * Utile per aggregazioni client-side che altrimenti si fermerebbero ai primi 100 record.
 * `hardCap` evita loop runaway su dataset enormi (default 2000).
 */
export async function fetchAllPages<T extends Denormalized = Denormalized>(
  type: string,
  query?: Omit<ListQuery, 'page'>,
  opts?: JsonApiClientOptions,
  hardCap = 2000,
): Promise<T[]> {
  const SIZE = 100;
  const all: T[] = [];
  let page = 1;
  let total = Infinity;
  while (all.length < total && all.length < hardCap) {
    const res = await fetchList<T>(type, { ...query, page: { number: page, size: SIZE } }, opts);
    all.push(...res.data);
    total = typeof res.total === 'number' && res.total > 0 ? res.total : all.length;
    if (res.data.length < SIZE) break;
    page += 1;
  }
  return all;
}

export async function fetchOne<T extends Denormalized = Denormalized>(
  type: string,
  id: string,
  query?: Pick<ListQuery, 'include' | 'fields'>
): Promise<T | null> {
  // GET single: niente injection — il BE risolve il tenant dal GUID (lookup
  // IgnoreQueryFilters), poi valida claim + descendant.
  const doc = await httpRequest<JsonApiDocument>(`${pathForType(type, id)}${buildQueryString(query)}`);
  return denormalize<T>(doc) as T | null;
}

export async function createOne<T extends Denormalized = Denormalized>(
  type: string,
  body: CreateBody,
  opts?: JsonApiClientOptions,
): Promise<T | null> {
  const id = body.id ?? generateUuid();
  // Cross-tenant: se l'override è attivo, aggiungo data.relationships.tenant
  // (a meno che il caller non lo abbia già impostato esplicitamente).
  const override = activeOverrideFor(type, opts);
  const tRel = tenantRelName(type);
  const relationships = override && !body.relationships?.[tRel]
    ? { ...(body.relationships ?? {}), [tRel]: { data: { type: 'tenants', id: override } } }
    : body.relationships;
  const payload: { data: Omit<JsonApiResource, 'id'> & { id?: string } } = {
    data: {
      type,
      id,
      attributes: body.attributes,
      relationships,
    },
  };
  const doc = await httpRequest<JsonApiDocument | undefined>(pathForType(type), { method: 'POST', body: payload });
  // JsonApiDotNetCore restituisce 204 No Content per POST con client-generated ID:
  // niente body in response, ma la risorsa e' stata creata con l'id che abbiamo
  // generato. Ricostruiamo lo stub dal payload locale cosi' il caller riceve sempre
  // un {id,type,...attributes} usable (la React Query subito dopo fara' la fetch
  // completa per il reload). Se il server invece restituisce un documento (201),
  // denormalizziamo come al solito.
  if (!doc) {
    return { type, id, ...body.attributes } as unknown as T;
  }
  return denormalize<T>(doc) as T | null;
}

export async function updateOne<T extends Denormalized = Denormalized>(type: string, body: UpdateBody): Promise<T | null> {
  // PATCH: niente injection cross-tenant — il BE risolve il tenant dal GUID
  // (lookup IgnoreQueryFilters), poi valida claim + descendant.
  const payload: { data: JsonApiResource } = {
    data: { type, id: body.id, attributes: body.attributes, relationships: body.relationships },
  };
  const doc = await httpRequest<JsonApiDocument | undefined>(pathForType(type, body.id), { method: 'PATCH', body: payload });
  // PATCH 204 → ricostruisco stub coerente con createOne (vedi nota sopra).
  if (!doc) {
    return { type, id: body.id, ...body.attributes } as unknown as T;
  }
  return denormalize<T>(doc) as T | null;
}

export interface DeleteOptions {
  /**
   * BE v1.8.2+: opt-in per cancellare anche i record dipendenti invece di ricevere 409.
   * Supportato sulle erp-sites (?cascade=true elimina dipendenti/orari/sedi figlie).
   */
  cascade?: boolean;
}

export async function deleteOne(type: string, id: string, opts?: DeleteOptions): Promise<void> {
  // DELETE: niente injection cross-tenant — il BE risolve il tenant dal GUID.
  const qs = opts?.cascade ? '?cascade=true' : '';
  await httpRequest(`${pathForType(type, id)}${qs}`, { method: 'DELETE' });
}
